import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import i18n from '@/i18n';
import { useLinkResumesToJob, useJobOffers } from '../../hooks/useJobOffers';
import { useAuth0 } from '@auth0/auth0-react';
import { ResumeItem, ResumeSearchFilters, JobStatus } from '../../types/job.types';
import { searchResumeBank, downloadResumeFromBank } from '../../services/job.service';
import { X, Loader2, User, Briefcase, Code, GraduationCap, Globe, Filter, Download, ChevronDown, ChevronUp, Search, Upload } from 'lucide-react';
import { UploadToResumeBankModal } from './UploadToResumeBankModal';
import { Tag } from '../ui/atoms/Tag';
import { SkillChip } from '../ui/atoms/SkillChip';
import { Input } from '../ui/atoms/Input';
import { Button } from '@/components/ui/atoms/Button';
import { SearchInput } from '../ui/search-input';
import { PaginationBar } from '../ui/molecules/PaginationBar';
import { Checkbox } from '../ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

interface BrowseResumeBankModalProps {
  jobId?: string;
  onClose: () => void;
  onSuccess?: () => void;
}

export const BrowseResumeBankModal: React.FC<BrowseResumeBankModalProps> = ({ 
  jobId, 
  onClose
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const linkMutation = useLinkResumesToJob();
  const { data: activeJobsData } = useJobOffers(true, JobStatus.ACTIVE);
  const { data: interviewingJobsData } = useJobOffers(true, JobStatus.INTERVIEWING);
  
  const [selectedJobId, setSelectedJobId] = useState<string>(jobId || '');
  const [jobSearchTerm, setJobSearchTerm] = useState('');
  const [isJobDropdownOpen, setIsJobDropdownOpen] = useState(false);
  
  const [resumes, setResumes] = useState<ResumeItem[]>([]);
  const [selectedResumeIds, setSelectedResumeIds] = useState<string[]>([]);
  const [isSelectAllMode, setIsSelectAllMode] = useState(false);
  const [isLoadingAllIds, setIsLoadingAllIds] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [expandedSkills, setExpandedSkills] = useState<Set<string>>(new Set());
  
  const [filters, setFilters] = useState<ResumeSearchFilters>({
    title: '',
    technical_skills: [],
    previous_sectors_fields: [],
    education: [],
    languages: [],
    min_experience: undefined,
    unlinked_only: false,
    exclude_job_offer_id: jobId || undefined // Exclude resumes already linked to this job
  });
  
  const [inputValues, setInputValues] = useState({
    title: '',
    technical_skill: '',
    sector: '',
    education: '',
    language: '',
    min_experience: ''
  });
  
  const [totalResults, setTotalResults] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 40;
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  
  // Upload modal state
  const [showUploadModal, setShowUploadModal] = useState(false);

  // Initial load
  useEffect(() => {
    handleSearch(1);
    setIsInitialLoad(false);
  }, []);

  // Auto-search when filters change (reset to page 1)
  useEffect(() => {
    if (isInitialLoad) return;
    
    const timeoutId = setTimeout(() => {
      setCurrentPage(1);
      handleSearch(1);
      setSelectedResumeIds([]); // Clear selections when filters change
      setIsSelectAllMode(false);
    }, 500);
    
    return () => clearTimeout(timeoutId);
  }, [filters]);

  const handleSearch = async (page: number = currentPage) => {
    setIsSearching(true);
    try {
      const token = await getAccessTokenSilently();
      const response = await searchResumeBank(token, {
        filters,
        page,
        page_size: pageSize
      });
      
      if (response.error) {
        toast.error(response.error || i18n.t('common:toasts.unexpected_error'));
        setResumes([]);
        setTotalResults(0);
        setTotalPages(1);
      } else if (response.data) {
        setResumes(response.data.resumes);
        setTotalResults(response.data.total);
        setTotalPages(response.data.total_pages);
        setCurrentPage(response.data.page);
      }
    } catch (error) {
      toast.error(error instanceof Error && error.message !== i18n.t('common:toasts.admin.search_failed', { lng: 'en' }) ? error.message : i18n.t('common:toasts.admin.search_failed'));
      setResumes([]);
      setTotalResults(0);
      setTotalPages(1);
    } finally {
      setIsSearching(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    handleSearch(newPage);
  };

  const handleAddFilter = (field: keyof ResumeSearchFilters, value: string) => {
    if (!value.trim()) return;
    
    if (field === 'title' || field === 'min_experience') {
      setFilters(prev => ({ ...prev, [field]: value }));
    } else {
      setFilters(prev => ({
        ...prev,
        [field]: [...(prev[field] as string[] || []), value]
      }));
    }
    
    setInputValues(prev => ({ ...prev, [field === 'title' ? 'title' : 'technical_skill']: '' }));
  };

  const handleTechnicalSkillInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const value = (e.target as HTMLInputElement).value;
    
    if (e.key === ' ' && value.trim()) {
      e.preventDefault();
      handleAddFilter('technical_skills', value.trim());
    } else if (e.key === 'Enter' && value.trim()) {
      e.preventDefault();
      handleAddFilter('technical_skills', value.trim());
    }
  };

  const handleRemoveFilter = (field: keyof ResumeSearchFilters, value?: string) => {
    if (field === 'title' || field === 'min_experience') {
      setFilters(prev => ({ ...prev, [field]: undefined }));
    } else if (value) {
      setFilters(prev => ({
        ...prev,
        [field]: (prev[field] as string[]).filter(v => v !== value)
      }));
    }
  };

  const handleToggleSelection = (resumeId: string) => {
    setIsSelectAllMode(false); // Exit select all mode when manually toggling
    setSelectedResumeIds(prev =>
      prev.includes(resumeId)
        ? prev.filter(id => id !== resumeId)
        : [...prev, resumeId]
    );
  };


  const handleLinkSelected = async () => {
    if (selectedResumeIds.length === 0) {
      toast.error(i18n.t('job:toasts.resume_select_required'));
      return;
    }

    if (!selectedJobId) {
      toast.error(i18n.t('job:toasts.resume_select_job'));
      return;
    }

    // Calculate how many will actually be linked (excluding already linked ones on current page)
    let alreadyLinkedOnCurrentPage = 0;
    
    selectedResumeIds.forEach(resumeId => {
      const resume = resumes.find(r => r.id === resumeId);
      if (resume?.associated_jobs?.some(job => job.job_id === selectedJobId)) {
        alreadyLinkedOnCurrentPage++;
      }
    });

    setIsLoading(true);
    const loadingToastId = toast.loading(
      i18n.t('job:toasts.resume_linking', { count: selectedResumeIds.length }) +
        (alreadyLinkedOnCurrentPage > 0
          ? i18n.t('job:toasts.resume_linking_skipped', { count: alreadyLinkedOnCurrentPage })
          : '')
    );

    try {
      const result = await linkMutation.mutateAsync({
        document_ids: selectedResumeIds,
        job_offer_id: selectedJobId
      });

      toast.dismiss(loadingToastId);
      
      // Show info toast (blue) with processing message
      const linkedCount = result.total_linked || selectedResumeIds.length;
      toast.info(i18n.t('job:toasts.resume_linked', { count: linkedCount }), {
        duration: 5000,
      });
      
      // Show errors if any
      if (result.errors && result.errors.length > 0) {
        const errorMessages = result.errors.map(e => e.error).join(', ');
        toast.warning(i18n.t('job:toasts.resume_link_partial_fail', { count: result.total_errors, errors: errorMessages }), {
          duration: 7000,
        });
      }
      
      onClose();
    } catch (error) {
      toast.dismiss(loadingToastId);
      toast.error(error instanceof Error && error.message.trim() ? error.message : i18n.t('common:toasts.unexpected_error'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadResume = async (e: React.MouseEvent, resumeId: string, fileName: string) => {
    e.stopPropagation();
    try {
      const token = await getAccessTokenSilently();
      const blob = await downloadResumeFromBank(token, resumeId);
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success(i18n.t('job:toasts.resume_download_success'));
    } catch (error) {
      console.error('Error downloading resume:', error);
      toast.error(i18n.t('job:toasts.resume_download_failed'));
    }
  };

  const toggleSkillsExpansion = (e: React.MouseEvent, resumeId: string) => {
    e.stopPropagation();
    setExpandedSkills(prev => {
      const newSet = new Set(prev);
      if (newSet.has(resumeId)) {
        newSet.delete(resumeId);
      } else {
        newSet.add(resumeId);
      }
      return newSet;
    });
  };

  const clearAllFilters = () => {
    setFilters({
      title: '',
      technical_skills: [],
      previous_sectors_fields: [],
      education: [],
      languages: [],
      min_experience: undefined,
      unlinked_only: false,
      job_offer_id: undefined,
      exclude_job_offer_id: jobId || undefined
    });
    setInputValues({
      title: '',
      technical_skill: '',
      sector: '',
      education: '',
      language: '',
      min_experience: ''
    });
    setSelectedResumeIds([]);
    setIsSelectAllMode(false);
  };

  const hasActiveFilters = filters.technical_skills?.length || 
    filters.min_experience || 
    filters.unlinked_only || 
    filters.job_offer_id;

  const handleSelectAll = async () => {
    // If there's only one page or already loaded all results, just select current page
    if (totalPages <= 1 || totalResults <= pageSize) {
      const allResumeIds = resumes.map(resume => resume.id);
      setSelectedResumeIds(allResumeIds);
      setIsSelectAllMode(false);
      return;
    }

    // For multiple pages, fetch all resume IDs
    setIsLoadingAllIds(true);
    try {
      const token = await getAccessTokenSilently();
      // Fetch with a very large page size to get all results
      const response = await searchResumeBank(token, {
        filters,
        page: 1,
        page_size: totalResults // Request all results
      });
      
      if (response.data && response.data.resumes) {
        const allResumeIds = response.data.resumes.map(resume => resume.id);
        setSelectedResumeIds(allResumeIds);
        setIsSelectAllMode(true);
      }
    } catch (error) {
      toast.error(i18n.t('job:toasts.resume_select_all_failed'));
      console.error('Error fetching all resume IDs:', error);
    } finally {
      setIsLoadingAllIds(false);
    }
  };

  const handleDeselectAll = () => {
    setSelectedResumeIds([]);
    setIsSelectAllMode(false);
  };

  const handleUploadComplete = () => {
    // Refresh the search to show new resumes after upload
    handleSearch(currentPage);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      {!showUploadModal && (
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-7xl h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-light-purple/30 to-purple/20">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-dark-invirtus font-heading flex items-center gap-2">
                <Search className="w-6 h-6 text-purple" />
                Browse Resume Bank
              </h2>
              <p className="text-sm text-gray-600 font-body mt-1">
                {totalResults > 0 ? (
                  <span>Found <span className="font-semibold text-purple">{totalResults}</span> resume{totalResults !== 1 ? 's' : ''}</span>
                ) : (
                  'Search and select resumes to add to your job'
                )}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {!jobId && (
                <Button
                  onClick={() => setShowUploadModal(true)}
                  type="contained"
                  intent="secondary"
                  className="font-heading"
                  disabled={isLoading}
                  icon={<Upload className="mr-2"/>}
                  label="Upload Resumes"
                />
              )}
              <Button
                  type="icon"
                  intent="muted"
                  icon={<X/>}
                  onClick={onClose}
                  disabled={isLoading}
                />
            </div>
          </div>
        </div>

        {/* Filters Section */}
        <div className="px-6 py-4 bg-gray-50/50 border-b border-gray-100">
          <div className="flex items-center justify-between mb-3">
            <Button
              type="text"
              intent="secondary"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              icon={<Filter />}
              rightIcon={showFilters ? <ChevronUp /> : <ChevronDown />}
              label="Filters"
              className="font-heading font-semibold"
            >
              {hasActiveFilters && (
                <Tag intent="primary" variant="solid" className="ml-1">
                  Active
                </Tag>
              )}
            </Button>
            {hasActiveFilters && (
              <Button
                type="text"
                intent="danger"
                size="xs"
                onClick={clearAllFilters}
                label="Clear all"
                className="underline font-heading"
              />
            )}
          </div>

          {showFilters && (
            <div className="grid grid-cols-3 gap-4">
              {/* Already Linked to Job */}
              <div className="space-y-2">
                <label className="text-xs font-semibold text-dark-invirtus font-heading uppercase tracking-wide">
                  Already Linked to Job
                </label>
                <Select
                  value={filters.job_offer_id || 'all'}
                  onValueChange={(value) => setFilters(prev => ({ 
                    ...prev, 
                    job_offer_id: value === 'all' ? undefined : value,
                    unlinked_only: false
                  }))}
                >
                  <SelectTrigger className="h-9 bg-white focus:ring-purple focus:border-purple">
                    <SelectValue placeholder="" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Any</SelectItem>
                    {activeJobsData?.items && activeJobsData.items.map((job) => (
                      <SelectItem key={job.id} value={job.id}>
                        {job.title}
                      </SelectItem>
                    ))}
                    {interviewingJobsData?.items && interviewingJobsData.items.map((job) => (
                      <SelectItem key={job.id} value={job.id}>
                        {job.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {/* Show only unlinked resumes checkbox */}
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={filters.unlinked_only || false}
                    onCheckedChange={(checked) => setFilters(prev => ({ 
                      ...prev, 
                      unlinked_only: checked as boolean,
                      job_offer_id: checked ? undefined : prev.job_offer_id
                    }))}
                    className="border-purple data-[state=checked]:bg-purple data-[state=checked]:border-purple"
                  />
                  <span className="text-sm font-medium text-dark-invirtus font-heading">
                    Show only unlinked resumes
                  </span>
                </label>
              </div>

              {/* Technical Skills */}
              <div className="space-y-2">
                <label className="text-xs font-semibold text-dark-invirtus font-heading uppercase tracking-wide">
                  Technical Skills <span className="text-gray-400 font-normal">(Space or Enter)</span>
                </label>
                <Input
                  type="text"
                  value={inputValues.technical_skill}
                  onChange={(e) => setInputValues(prev => ({ ...prev, technical_skill: e.target.value }))}
                  onKeyDown={handleTechnicalSkillInput}
                  placeholder="React, Python, AWS..."
                />
                {filters.technical_skills && filters.technical_skills.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {filters.technical_skills.map((skill) => (
                      <Tag
                        intent="primary"
                        variant="solid"
                        key={skill} 
                        className="bg-purple text-white hover:bg-purple/80 cursor-pointer border border-purple"
                        onClick={() => handleRemoveFilter('technical_skills', skill)}
                      >
                        {skill}
                        <X className="w-3 h-3 ml-1" />
                      </Tag>
                    ))}
                  </div>
                )}
              </div>

              {/* Min Experience */}
              <div className="space-y-2">
                <label className="text-xs font-semibold text-dark-invirtus font-heading uppercase tracking-wide">
                  Min Experience (years)
                </label>
                <Input
                  type="number"
                  min="0"
                  value={inputValues.min_experience}
                  onChange={(e) => {
                    const value = e.target.value;
                    setInputValues(prev => ({ ...prev, min_experience: value }));
                    if (value) {
                      // Use max of 0 and years of exp - 1
                      const filterValue = Math.max(0, parseInt(value) - 1);
                      setFilters(prev => ({ ...prev, min_experience: filterValue }));
                    } else {
                      setFilters(prev => ({ ...prev, min_experience: undefined }));
                    }
                  }}
                  placeholder="e.g., 5"
                />
              </div>
            </div>
          )}
        </div>

        {/* Resume List */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {isSearching ? (
            <div className="flex flex-col items-center justify-center h-full">
              <Loader2 className="w-12 h-12 text-purple animate-spin mb-4" />
              <p className="text-gray-600 font-body">Searching resume bank...</p>
            </div>
          ) : resumes.length > 0 ? (
            <>
              {/* Selection Controls */}
              <div className="flex items-center justify-between py-3 px-4 mb-4 bg-light-purple/30 rounded-lg border border-purple/20">
                <div className="flex items-center gap-3">
                  {selectedResumeIds.length > 0 && (
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-dark-invirtus font-heading">
                        <span className="text-purple text-lg font-bold">{selectedResumeIds.length}</span>
                        {' '}resume{selectedResumeIds.length !== 1 ? 's' : ''} selected
                      </span>
                      {isSelectAllMode && (
                        <span className="text-xs text-gray-600 font-body italic">
                          Selected across all pages
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {isLoadingAllIds ? (
                    <div className="flex items-center gap-2 text-xs text-purple font-heading">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      <span>Loading all resumes...</span>
                    </div>
                  ) : (
                    <>
                      {selectedResumeIds.length > 0 && (isSelectAllMode || selectedResumeIds.length >= resumes.length) ? (
                        <button
                          onClick={handleDeselectAll}
                          className="text-xs text-gray-600 hover:text-red underline font-heading font-semibold"
                          disabled={isLoadingAllIds}
                        >
                          Deselect All
                        </button>
                      ) : (
                        <button
                          onClick={handleSelectAll}
                          className="text-xs text-purple hover:text-purple/80 underline font-heading font-semibold disabled:opacity-50"
                          disabled={isLoadingAllIds}
                        >
                          Select All{totalPages > 1 ? ` (${totalResults})` : ''}
                        </button>
                      )}
                      {selectedResumeIds.length > 0 && !isSelectAllMode && selectedResumeIds.length < resumes.length && (
                        <Button
                          type="text"
                          intent="danger"
                          size="xs"
                          onClick={handleDeselectAll}
                          label="Clear"
                          className="underline font-heading"
                          disabled={isLoadingAllIds}
                        />
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Resume Grid */}
              <div className="grid grid-cols-2 gap-4">
                {resumes.map((resume) => {
                  const info = resume.extracted_data.extracted_info;
                  const isSelected = selectedResumeIds.includes(resume.id);
                  const yearsOfExperience = resume.years_of_experience || 0;
                  const isExpanded = expandedSkills.has(resume.id);
                  const displayedSkills = isExpanded ? info.technical_skills : info.technical_skills?.slice(0, 4);

                  return (
                    <div
                      key={resume.id}
                      onClick={() => handleToggleSelection(resume.id)}
                      className={`group relative p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                        isSelected 
                          ? 'border-purple bg-purple/5 shadow-md' 
                          : 'border-gray-200 hover:border-purple/50 hover:shadow-md bg-white'
                      }`}
                    >
                      {/* Selection Indicator */}
                      <div className="absolute top-3 left-3" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => handleToggleSelection(resume.id)}
                          className="border-purple data-[state=checked]:bg-purple data-[state=checked]:border-purple"
                        />
                      </div>

                      {/* Download Button — positioned wrapper (Button is relative) */}
                      <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button
                          type="icon"
                          intent="muted"
                          size="sm"
                          onClick={(e) => handleDownloadResume(e, resume.id, resume.extracted_data.document_name)}
                          aria-label="Download Resume"
                          title="Download Resume"
                          icon={<Download />}
                        />
                      </div>

                      {/* Content */}
                      <div className="mt-8">
                        {/* Name & Email */}
                        <div className="mb-3">
                          <h4 className="font-bold text-dark-invirtus font-heading text-base mb-1">
                            {info.first_name} {info.last_name}
                          </h4>
                          <p className="text-xs text-gray-600 font-body">{info.email}</p>
                        </div>

                        {/* Title */}
                        {info.title && (
                          <div className="flex items-start gap-2 mb-2">
                            <Briefcase className="w-4 h-4 text-purple flex-shrink-0 mt-0.5" />
                            <span className="text-sm text-gray-700 font-body line-clamp-1">{info.title}</span>
                          </div>
                        )}

                        {/* Technical Skills */}
                        {info.technical_skills && info.technical_skills.length > 0 && (
                          <div className="mb-2">
                            <div className="flex items-start gap-2 mb-1.5">
                              <Code className="w-4 h-4 text-purple flex-shrink-0 mt-0.5" />
                              <span className="text-xs font-semibold text-gray-600 font-heading">Skills</span>
                            </div>
                            <div className="flex flex-wrap gap-1.5 ml-6">
                              {displayedSkills?.map((skill, idx) => (
                                <SkillChip key={idx} label={skill} />
                              ))}
                              {info.technical_skills.length > 4 && (
                                <Button
                                  type="text"
                                  intent="primary"
                                  size="xs"
                                  onClick={(e) => toggleSkillsExpansion(e, resume.id)}
                                  label={isExpanded ? 'less' : `+${info.technical_skills.length - 4} more`}
                                  className="underline font-semibold !px-0"
                                />
                              )}
                            </div>
                          </div>
                        )}

                        {/* Education */}
                        {info.education && info.education.length > 0 && (
                          <div className="flex items-start gap-2 mb-2">
                            <GraduationCap className="w-4 h-4 text-purple flex-shrink-0 mt-0.5" />
                            <span className="text-sm text-gray-700 font-body line-clamp-1">{info.education[0]}</span>
                          </div>
                        )}

                        {/* Languages */}
                        {info.languages && info.languages.length > 0 && (
                          <div className="flex items-start gap-2 mb-3">
                            <Globe className="w-4 h-4 text-purple flex-shrink-0 mt-0.5" />
                            <span className="text-sm text-gray-700 font-body">{info.languages.slice(0, 3).join(', ')}</span>
                          </div>
                        )}

                        {/* Associated Job Offers */}
                        {resume.associated_jobs && resume.associated_jobs.length > 0 && (
                          <div className="mb-2">
                            <div className="flex items-start gap-2 mb-1.5">
                              <Briefcase className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                              <span className="text-xs font-semibold text-gray-600 font-heading">Linked to Jobs</span>
                            </div>
                            <div className="flex flex-wrap gap-1.5 ml-6">
                              {resume.associated_jobs.map((job) => (
                                <Tag
                                  intent="success"
                                  variant="subtle"
                                  key={job.job_id} 
                                  className="bg-green-50 text-green-700 border border-green-200 hover:bg-green-100"
                                  title={job.job_title}
                                >
                                  {job.job_title.length > 20 ? `${job.job_title.substring(0, 20)}...` : job.job_title}
                                </Tag>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Experience Badge */}
                        {yearsOfExperience > 0 && (
                          <div className="flex justify-end pt-2 border-t border-gray-100">
                            <Tag intent="primary" variant="subtle">
                              {Math.ceil(yearsOfExperience)} {Math.ceil(yearsOfExperience) === 1 ? 'year' : 'years'} of experience
                            </Tag>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-full">
              <div className="w-16 h-16 bg-light-purple/50 rounded-full flex items-center justify-center mb-4">
                <User className="w-8 h-8 text-purple" />
              </div>
              <p className="text-gray-600 font-body text-lg font-medium">No resumes found</p>
              <p className="text-gray-500 font-body text-sm mt-1">Try adjusting your filters</p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <PaginationBar
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalResults}
            onPageChange={handlePageChange}
            disabled={isSearching}
            className="border-gray-100 bg-gray-50/50"
          />
        )}

        {/* Footer Actions */}
        <div className={`border-t border-gray-200 transition-all ${
          selectedResumeIds.length > 0 ? 'bg-purple/10' : 'bg-white'
        }`}>
          {/* Job Selector - shown inline when opened from Dashboard AND resumes are selected */}
          {!jobId && selectedResumeIds.length > 0 && (() => {
            // Get all available jobs (Active and Interviewing, excluding Archived)
            const allActiveJobs = activeJobsData?.items || [];
            const allInterviewingJobs = interviewingJobsData?.items || [];

            // Filter jobs based on search term
            const searchLower = jobSearchTerm.toLowerCase();
            const filteredActiveJobs = allActiveJobs.filter(job =>
              job.title.toLowerCase().includes(searchLower) ||
              (job.companyName || '').toLowerCase().includes(searchLower)
            );
            const filteredInterviewingJobs = allInterviewingJobs.filter(job =>
              job.title.toLowerCase().includes(searchLower) ||
              (job.companyName || '').toLowerCase().includes(searchLower)
            );

            // Calculate how many selected resumes are already linked to the chosen job
            let alreadyLinkedCount = 0;
            let willBeLinkedCount = 0;
            let canCalculateExact = true;
            
            if (selectedJobId) {
              selectedResumeIds.forEach(resumeId => {
                const resume = resumes.find(r => r.id === resumeId);
                
                // If we don't have resume data (selected from another page), we can't calculate exactly
                if (!resume) {
                  canCalculateExact = false;
                  return;
                }
                
                const isAlreadyLinked = resume?.associated_jobs?.some(job => job.job_id === selectedJobId);
                if (isAlreadyLinked) {
                  alreadyLinkedCount++;
                } else {
                  willBeLinkedCount++;
                }
              });
            }

            return (
              <div className="px-6 py-3 border-b border-gray-200/50">
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <label className="text-xs font-semibold text-dark-invirtus font-heading whitespace-nowrap uppercase tracking-wide">
                      Link to Job: <span className="text-red-500">*</span>
                    </label>
                    <div className="flex-1 relative">
                      {selectedJobId && !isJobDropdownOpen ? (
                        /* Selected Job Badge - replaces search bar */
                        <div className="flex items-center gap-2 px-3 py-2 bg-purple/10 border-2 border-purple rounded-md">
                          <Button
                            type="icon"
                            intent="muted"
                            size="xs"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedJobId('');
                              setJobSearchTerm('');
                              setIsJobDropdownOpen(true);
                            }}
                            aria-label="Clear job selection"
                            icon={<X />}
                            className="flex-shrink-0"
                          />
                          <Briefcase className="w-4 h-4 text-purple flex-shrink-0" />
                          <div 
                            className="flex-1 min-w-0 cursor-pointer"
                            onClick={() => {
                              setSelectedJobId('');
                              setJobSearchTerm('');
                              setIsJobDropdownOpen(true);
                            }}
                          >
                            <div className="text-sm font-semibold text-dark-invirtus font-heading truncate">
                              {allActiveJobs.concat(allInterviewingJobs).find(j => j.id === selectedJobId)?.title || 'Selected Job'}
                            </div>
                            <div className="text-xs text-gray-600 font-body">
                              {allActiveJobs.concat(allInterviewingJobs).find(j => j.id === selectedJobId)?.companyName || ''}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <SearchInput
                          placeholder="Type to filter jobs..."
                          value={jobSearchTerm}
                          onChange={(v) => {
                            setJobSearchTerm(v);
                            if (!isJobDropdownOpen) {
                              setIsJobDropdownOpen(true);
                            }
                          }}
                          onFocus={() => {
                            setIsJobDropdownOpen(true);
                          }}
                          onBlur={() => {
                            // Delay to allow click on dropdown item
                            setTimeout(() => {
                              setIsJobDropdownOpen(false);
                              setJobSearchTerm('');
                            }, 200);
                          }}
                          size="sm"
                        />
                      )}
                      {isJobDropdownOpen && (
                        <div className="absolute bottom-full left-0 right-0 mb-1 bg-white border-2 border-purple/30 rounded-md shadow-lg max-h-[320px] overflow-y-auto z-50">
                          {filteredActiveJobs.length > 0 && (
                            <>
                              <div className="px-2 py-1.5 text-xs font-semibold text-gray-500 uppercase bg-gray-50 sticky top-0">Active Jobs</div>
                              {filteredActiveJobs.map((job) => (
                                <div
                                  key={job.id}
                                  onMouseDown={(e) => {
                                    e.preventDefault(); // Prevent blur
                                    setSelectedJobId(job.id);
                                    setJobSearchTerm('');
                                    setIsJobDropdownOpen(false);
                                  }}
                                  className="px-3 py-2 hover:bg-purple/10 cursor-pointer border-b border-gray-100 last:border-b-0"
                                >
                                  <div className="font-medium text-sm text-dark-invirtus">{job.title}</div>
                                  <div className="text-xs text-gray-500">{job.companyName || 'No company'}</div>
                                </div>
                              ))}
                            </>
                          )}
                          {filteredInterviewingJobs.length > 0 && (
                            <>
                              <div className="px-2 py-1.5 text-xs font-semibold text-gray-500 uppercase bg-gray-50 sticky top-0">Interviewing Jobs</div>
                              {filteredInterviewingJobs.map((job) => (
                                <div
                                  key={job.id}
                                  onMouseDown={(e) => {
                                    e.preventDefault(); // Prevent blur
                                    setSelectedJobId(job.id);
                                    setJobSearchTerm('');
                                    setIsJobDropdownOpen(false);
                                  }}
                                  className="px-3 py-2 hover:bg-purple/10 cursor-pointer border-b border-gray-100 last:border-b-0"
                                >
                                  <div className="font-medium text-sm text-dark-invirtus">{job.title}</div>
                                  <div className="text-xs text-gray-500">{job.companyName || 'No company'}</div>
                                </div>
                              ))}
                            </>
                          )}
                          {filteredActiveJobs.length === 0 && filteredInterviewingJobs.length === 0 && (
                            <div className="px-2 py-1.5 text-xs text-gray-500 italic">
                              {jobSearchTerm ? 'No jobs match your search' : 'No jobs available'}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Link Status Message */}
                  {selectedJobId && (
                    <div className="ml-2 text-xs font-body">
                      {!canCalculateExact ? (
                        <span className="text-dark-invirtus">
                          <span className="font-bold text-purple">{selectedResumeIds.length}</span> resume{selectedResumeIds.length !== 1 ? 's' : ''} selected.{' '}
                          <span className="text-gray-600 italic">
                            (Already linked resumes will be skipped automatically)
                          </span>
                        </span>
                      ) : willBeLinkedCount > 0 ? (
                        <span className="text-dark-invirtus">
                          <span className="font-bold text-purple">{willBeLinkedCount}</span> out of{' '}
                          <span className="font-bold">{selectedResumeIds.length}</span> resume{selectedResumeIds.length !== 1 ? 's' : ''} will be linked
                          {alreadyLinkedCount > 0 && (
                            <span className="text-gray-600">
                              {' '}(<span className="font-semibold">{alreadyLinkedCount}</span> already linked to this job)
                            </span>
                          )}
                        </span>
                      ) : (
                        <span className="text-amber-600 font-semibold">
                          All {selectedResumeIds.length} selected resume{selectedResumeIds.length !== 1 ? 's are' : ' is'} already linked to this job
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })()}
          
          <div className="px-6 py-4">
            <div className="flex justify-between items-center">
              <div className="text-sm font-body">
                {selectedResumeIds.length > 0 ? (
                  <span className="text-dark-invirtus font-semibold">
                    <span className="text-purple text-lg">{selectedResumeIds.length}</span> resume{selectedResumeIds.length !== 1 ? 's' : ''} selected
                  </span>
                ) : (
                  <span className="text-gray-500">No resumes selected</span>
                )}
              </div>
              <div className="flex gap-3">
                <Button
                  onClick={onClose}
                  type="outlined"
                  intent="secondary"
                  disabled={isLoading}
                  className="font-heading"
                  label='Cancel'
                />
                <Button
                  onClick={handleLinkSelected}
                  disabled={isLoading || selectedResumeIds.length === 0 || (!jobId && !selectedJobId)}
                  loading={isLoading}
                  className="font-heading"
                  type="contained"
                  intent="secondary"
                  title={
                    !jobId && !selectedJobId 
                      ? 'Please select a job offer first' 
                      : selectedResumeIds.length === 0 
                      ? 'Please select at least one resume'
                      : ''
                  }
                  label={
                    isLoading
                      ? 'Linking...'
                      : `Link ${selectedResumeIds.length > 0 ? selectedResumeIds.length : ''} Resume${selectedResumeIds.length !== 1 ? 's' : ''}`
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Upload to Resume Bank Modal */}
      {showUploadModal && (
        <UploadToResumeBankModal
          onClose={() => {
            setShowUploadModal(false);
            handleUploadComplete();
          }}
        />
      )}
    </div>
  );
};
